import { canonicalTimezone } from '../../common/intlFormatters'
import { TIMEZONE_LABELS } from './constants'

const OFFSET_OPTIONS: Intl.DateTimeFormatOptions = { timeZoneName: 'shortOffset', hour: '2-digit', minute: '2-digit', hour12: false }

const OFFSET_FORMATTERS = new Map<string, Intl.DateTimeFormat | null>()

const offsetFormatter = (ianaTimezone: string): Intl.DateTimeFormat | null => {
  const cached = OFFSET_FORMATTERS.get(ianaTimezone)
  if (cached !== undefined) {
    return cached
  }
  let formatter: Intl.DateTimeFormat | null
  try {
    formatter = new Intl.DateTimeFormat('en-US', { ...OFFSET_OPTIONS, timeZone: ianaTimezone })
  } catch {
    formatter = null
  }
  OFFSET_FORMATTERS.set(ianaTimezone, formatter)
  return formatter
}

export const getGmtOffsetLabel = (ianaTimezone: string, date = new Date()) => {
  const dtf = offsetFormatter(ianaTimezone)
  if (!dtf) {
    return '(GMT+00:00)'
  }

  const tzName = dtf.formatToParts(date).find((p) => p.type === 'timeZoneName')?.value
  if (!tzName) {
    return '(GMT+00:00)'
  }

  const match = tzName.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/)
  if (!match) {
    return '(GMT+00:00)'
  }

  const sign = match[1]
  const hh = String(Number(match[2])).padStart(2, '0')
  const mm = String(Number(match[3] ?? '0')).padStart(2, '0')
  return `(GMT${sign}${hh}:${mm})`
}

export const TIMEZONE_LABEL_BY_ZONE: Record<string, string> = Object.fromEntries(
  Object.entries(TIMEZONE_LABELS).map(([label, ianaTimezone]) => [ianaTimezone, label]),
)

export const getTimezoneLabel = (ianaTimezone: string): string => TIMEZONE_LABEL_BY_ZONE[ianaTimezone] ?? ianaTimezone

const runtimeTimezones = (): string[] => {
  try {
    return Intl.supportedValuesOf('timeZone')
  } catch {
    return []
  }
}

interface TimezoneCatalogue {
  zones: string[]
  canonicals: Set<string>
}

let catalogue: TimezoneCatalogue | null = null

const getCatalogue = (): TimezoneCatalogue => {
  if (catalogue) {
    return catalogue
  }
  const canonicals = new Set<string>()
  const zones: string[] = []

  const add = (ianaTimezone: string) => {
    const canonical = canonicalTimezone(ianaTimezone)
    if (!canonical || canonicals.has(canonical)) {
      return
    }
    canonicals.add(canonical)
    zones.push(ianaTimezone)
  }

  Object.values(TIMEZONE_LABELS).forEach(add)
  runtimeTimezones().forEach(add)

  catalogue = { zones, canonicals }
  return catalogue
}

export const buildTimezoneOptions = (selectedTimezone?: string): string[] => {
  const { zones, canonicals } = getCatalogue()
  if (!selectedTimezone) {
    return zones
  }
  const canonical = canonicalTimezone(selectedTimezone)
  if (!canonical || canonicals.has(canonical)) {
    return zones
  }
  return [...zones, selectedTimezone]
}
